/**
 * Audio do sistema para a transmissao de tela, capturado nativamente pelo
 * processo principal do Electron EXCLUINDO o proprio LFRCORD (Windows
 * Process Loopback com exclusao de arvore de processo — a mesma tecnica que
 * o Discord usa). So existe dentro do app desktop: `window.lfrcordDesktop`
 * nao existe no navegador comum, e quem chama esta funcao la fora recebe
 * `null` e cai de volta pro caminho antigo (systemAudio do proprio
 * getDisplayMedia, que nao tem como excluir so o LFRCORD).
 *
 * O processo nativo entrega blocos de PCM (Float32, estereo intercalado,
 * 48kHz) via IPC; aqui eles viram uma trilha de audio de verdade atraves de
 * um AudioWorklet que so reproduz o que chega, ligado a um
 * MediaStreamAudioDestinationNode.
 */
import nativeWorkletUrl from "@/media/nativeAudioWorklet.js?url";
import { manterContextoVivo } from "@/media/audioContextGuard";

export type NativeSystemAudioCapture = {
  track: MediaStreamTrack;
  stop: () => void;
};

const READY_TIMEOUT_MS = 6_000;
/**
 * A ativacao da engine de audio do Windows pode falhar "a frio" (primeira
 * vez que o processo pede um handle WASAPI depois de abrir, ou logo depois
 * de instalar) sem que isso signifique falha real de verdade — uma segunda
 * tentativa, alguns instantes depois, costuma resolver sozinha. So desiste
 * de vez (cai pro aviso de "sem audio do sistema") depois de esgotar isso.
 */
const MAX_ATTEMPTS = 3;
const RETRY_DELAY_MS = 500;

const sleep = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

async function attemptNativeCapture(
  bridge: NonNullable<Window["lfrcordDesktop"]>["systemAudioCapture"],
  node: AudioWorkletNode,
): Promise<{ unsubscribe: () => void } | null> {
  const subscription: { unsubscribe: () => void } = { unsubscribe: () => undefined };
  const ready = await new Promise<boolean>(resolve => {
    let settled = false;
    const finish = (ok: boolean) => {
      if (settled) return;
      settled = true;
      resolve(ok);
    };
    const timer = setTimeout(() => finish(false), READY_TIMEOUT_MS);
    subscription.unsubscribe = bridge.start(event => {
      if (event.type === "ready") {
        clearTimeout(timer);
        finish(true);
      } else if (event.type === "error") {
        clearTimeout(timer);
        finish(false);
      } else if (event.type === "data") {
        node.port.postMessage(event.samples, [event.samples.buffer]);
      }
    });
  });
  if (ready) return subscription;
  subscription.unsubscribe();
  return null;
}

export async function startNativeSystemAudio(): Promise<NativeSystemAudioCapture | null> {
  const bridge = window.lfrcordDesktop?.systemAudioCapture;
  if (!bridge) return null;

  let context: AudioContext;
  try {
    context = new AudioContext({ sampleRate: 48_000, latencyHint: "interactive" });
    await context.audioWorklet.addModule(nativeWorkletUrl);
  } catch {
    return null;
  }

  const node = new AudioWorkletNode(context, "lfrcord-native-playback", {
    numberOfInputs: 0,
    numberOfOutputs: 1,
    outputChannelCount: [2],
  });
  const destination = context.createMediaStreamDestination();
  node.connect(destination);

  let subscription: { unsubscribe: () => void } | null = null;
  for (let attempt = 1; attempt <= MAX_ATTEMPTS && !subscription; attempt++) {
    subscription = await attemptNativeCapture(bridge, node);
    if (!subscription && attempt < MAX_ATTEMPTS) await sleep(RETRY_DELAY_MS);
  }

  if (!subscription) {
    node.disconnect();
    void context.close().catch(() => undefined);
    return null;
  }

  const track = destination.stream.getAudioTracks()[0];
  if (!track) {
    subscription.unsubscribe();
    node.disconnect();
    void context.close().catch(() => undefined);
    return null;
  }

  /*
    Mesma rede de protecao do microfone, e pelo mesmo motivo: suspenso, este
    contexto entrega uma trilha viva que so carrega silencio, e a transmissao
    fica muda sem nenhum aviso. Aqui a chance e ate maior — abrir a captura de
    loopback e o que costuma sacudir o servico de audio do Windows.
  */
  const pararVigia = manterContextoVivo(context);

  return {
    track,
    stop: () => {
      pararVigia();
      subscription.unsubscribe();
      node.disconnect();
      void context.close().catch(() => undefined);
    },
  };
}
